import React, { ChangeEvent, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAuth } from "../../context/AuthContext.tsx";
import Papa from "papaparse";
import * as S from "./onboarding.style.ts";
import { budgetAtom } from "../../hook/BudgetAtom.ts";
import { incomeAtom } from "../../hook/IncomeAtom.ts";
import { expenseAtom } from "../../hook/ExpenseAtom.ts";
import { cashAccountsAtom } from "../../hook/CashAccountsAtom.ts";
import { incomeEventsAtom } from "../../hook/IncomeEventsAtom.ts";
import { recurringIncomeAtom } from "../../hook/RecurringIncomeAtom.ts";
import { ownerPayConfigAtom } from "../../hook/OwnerPayConfigAtom.ts";
import { userAtom } from "../../hook/UserAtom.ts";
import {
  createInitialBudget,
  formatBudgetData,
  formatBudgetItem,
} from "../../functions/budget.ts";
import { monthlyEquivalent } from "../../functions/cashflow.ts";
import {
  INCOME_MODE_OPTIONS,
  modeLabels,
} from "../../functions/cashflowCopy.ts";
import {
  checkIsExpiredSession,
  getDateInfo,
  loggedInHomepage,
} from "../../functions/helper.ts";
import Button from "../../components/Button/Button.tsx";
import Link from "../../components/Link/Link.tsx";
import Loading from "../../components/Loading/Loading.tsx";
import RecurringIncome from "../../components/RecurringIncome/RecurringIncome.tsx";
import SessionExpired from "../../components/SessionExpired/SessionExpired.tsx";
import { createBudget } from "../../requests/budget.ts";
import { trackError, trackEvent } from "../../functions/mixpanel.ts";
import {
  BudgetDataItem,
  CreateBudgetItems,
  IncomeEvent,
  IncomeMode,
} from "../../types.ts";
import { persistOwnerPayConfig } from "../../functions/cashflowSync.ts";

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const TOTAL_STEPS = 4;

const Onboarding = () => {
  const auth = useAuth();
  const { getAccessTokenSilently } = auth;
  const [budget, setBudget] = useAtom(budgetAtom);
  const [currentUser, setCurrentUser] = useAtom(userAtom);
  const [budgetIncome, setBudgetIncome] = useAtom(incomeAtom);
  const [budgetExpense, setBudgetExpense] = useAtom(expenseAtom);
  const [incomeEvents, setIncomeEvents] = useAtom(incomeEventsAtom);
  const recurring = useAtomValue(recurringIncomeAtom);
  const [config, setConfig] = useAtom(ownerPayConfigAtom);
  const setCashAccounts = useSetAtom(cashAccountsAtom);
  const { currentYear, currentMonth } = getDateInfo();
  const labels = modeLabels(config);

  const [step, setStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [checking, setChecking] = useState<string>("");
  const [buffer, setBuffer] = useState<string>("");
  const [taxReserve, setTaxReserve] = useState<string>("");
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);

  const mode = config.mode;
  const activeRules = recurring.filter((rule) => rule.active);
  const usesRecurring = mode !== "irregular";

  useEffect(() => {
    const storedIncome = localStorage.getItem("budgetIncome");
    if (storedIncome) {
      setBudgetIncome(JSON.parse(storedIncome));
    }
    const storedExpense = localStorage.getItem("budgetExpense");
    if (storedExpense) {
      setBudgetExpense(JSON.parse(storedExpense));
    }
  }, []);

  const handleFileUpload = (
    event: ChangeEvent<HTMLInputElement>,
    kind: "income" | "expense",
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        const rows: CreateBudgetItems[] = [];

        results.data.forEach((item: any) => {
          if (item["Amount (Monthly)"] === "") {
            return;
          }

          rows.push({
            label: item["Item"],
            value: item["Amount (Monthly)"],
            checked: false,
            frequency: "Monthly",
            cadence: "Current Month",
          });
        });

        const entries = formatBudgetItem(rows, currentMonth, currentYear);
        if (kind === "income") {
          setBudgetIncome(entries);
          localStorage.setItem("budgetIncome", JSON.stringify(entries));
        } else {
          setBudgetExpense(entries);
          localStorage.setItem("budgetExpense", JSON.stringify(entries));
        }
      },
    });
  };

  const chooseMode = (chosen: IncomeMode) => {
    const selectedConfig = {
      mode: chosen,
      ...(chosen === "regular"
        ? { taxReservePercent: 0, method: "trailing_average" as const }
        : {}),
    };

    setConfig((prev) => ({
      ...prev,
      ...selectedConfig,
    }));

    persistOwnerPayConfig(auth, { ...config, ...selectedConfig });

    trackEvent("Onboarding - income path", { mode: chosen });
    setStep(1);
  };

  // `createBudget` needs both income and expense rows. Regular / mixed users
  // don't upload an income CSV, so we synthesise one monthly-equivalent row per
  // recurring rule. These live only in the budget record; the cash-flow engine
  // reads income from the dated log / recurring rules, never from the budget.
  const effectiveIncome = (): BudgetDataItem[] => {
    if (budgetIncome.length > 0) {
      return budgetIncome;
    }

    return activeRules.map((rule) => ({
      label: rule.label || "Paycheck",
      value: monthlyEquivalent(rule.amount, rule.frequency),
      paid: false,
      frequency: "Monthly",
      budget_id: null,
      budget_date_id: null,
    }));
  };

  // Spread the "typical monthly income" across the trailing window (plus the
  // current month) so the dashboard has history to work from on day one —
  // trailing stats only look at completed months.
  const seedIncomeEvents = () => {
    if (
      incomeEvents.length > 0 ||
      activeRules.length > 0 ||
      budgetIncome.length === 0
    ) {
      return;
    }

    const now = new Date();
    const months = Math.max(1, config.trailingMonths);
    const seeded: IncomeEvent[] = [];

    for (let back = months; back >= 0; back -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - back, 15);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-15`;

      budgetIncome.forEach((item) =>
        seeded.push({
          id: uuid(),
          date: iso,
          amount: item.value,
          label: item.label,
          status: "received",
          confidence: 1,
        }),
      );
    }

    setIncomeEvents(seeded);
  };

  const finish = async () => {
    const initialBudget = formatBudgetData(effectiveIncome(), budgetExpense);
    setError("");
    setIsLoading(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const insertIds = await createBudget(accessToken, initialBudget);
      const formattedBudget = createInitialBudget(
        initialBudget,
        insertIds?.budget_ids || [],
      );

      if (!formattedBudget.length) {
        throw new Error("createBudget returned no budget");
      }

      setBudget(formattedBudget);
      setCashAccounts({
        checkingBalance: Number(checking) || 0,
        bufferBalance: Number(buffer) || 0,
        taxBalance: mode === "regular" ? 0 : Number(taxReserve) || 0,
        asOf: new Date().toISOString().slice(0, 10),
      });
      seedIncomeEvents();

      currentUser && setCurrentUser({ ...currentUser, hasBudget: true });
      trackEvent("Submitted Initial Budget", { mode });
      localStorage.removeItem("budgetIncome");
      localStorage.removeItem("budgetExpense");
    } catch (err) {
      trackError("Onboarding - finish:", { result: err });
      setIsLoading(false);

      if (checkIsExpiredSession(err)) {
        setIsSessionExpired(true);
      } else {
        setError(
          "We couldn't save your setup. Please check your connection and try again.",
        );
      }
    }
  };

  if (currentUser?.hasBudget && !budget.length) {
    return <Navigate to="/cash-flow" replace />;
  }

  if (budget.length) {
    window.location.href = loggedInHomepage(currentUser);
    return <Loading />;
  }

  if (isLoading) {
    return <Loading />;
  }

  const incomeReady =
    budgetIncome.length > 0 || (usesRecurring && activeRules.length > 0);
  const stepValid =
    step === 0 ||
    (step === 1 && incomeReady) ||
    (step === 2 && budgetExpense.length > 0) ||
    step === 3;

  return (
    <S.Wrapper>
      <S.Progress>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <span key={i} className={i <= step ? "done" : ""} />
        ))}
      </S.Progress>

      {step === 0 && (
        <S.Step>
          <h2>How does your income arrive?</h2>
          <p>This tailors the wording and which tools you see.</p>
          <S.Choices>
            {INCOME_MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={mode === option.value ? "selected" : ""}
                onClick={() => chooseMode(option.value)}
              >
                <span className="title">{option.title}</span>
                <span className="blurb">{option.blurb}</span>
              </button>
            ))}
          </S.Choices>
        </S.Step>
      )}

      {step === 1 && (
        <S.Step>
          <h2>Your income</h2>
          {usesRecurring ? (
            <>
              <p>
                Add your paycheck. Set it once — every payday fills itself in.
              </p>
              <RecurringIncome />
              <p>
                Prefer a spreadsheet? You can also import a typical month on the
                next screens and adjust later.
              </p>
            </>
          ) : (
            <S.Uploads>
              <p>
                Upload a typical month of income. You&apos;ll log individual
                payments once you&apos;re in.
              </p>
              <Link
                url="/csv/simple-budgeting-template-income.csv"
                label="Download Income CSV"
                classType="button"
                target="_blank"
                download
              >
                Download Income CSV
              </Link>
              <div className="field">
                <label htmlFor="incomeCsv">Upload income CSV</label>
                <input
                  type="file"
                  accept=".csv"
                  id="incomeCsv"
                  onChange={(e) => handleFileUpload(e, "income")}
                />
                {budgetIncome.length > 0 && (
                  <span className="done">
                    {budgetIncome.length} income items loaded
                  </span>
                )}
              </div>
            </S.Uploads>
          )}
        </S.Step>
      )}

      {step === 2 && (
        <S.Step>
          <h2>Your recurring expenses</h2>
          <S.Uploads>
            <p>
              Upload your monthly bills and spending. Mark which ones are
              essential later — that&apos;s what your runway is measured
              against.
            </p>
            <Link
              url="/csv/simple-budgeting-template-expense.csv"
              label="Download Expense CSV"
              classType="button"
              target="_blank"
              download
            >
              Download Expense CSV
            </Link>
            <div className="field">
              <label htmlFor="expenseCsv">Upload expense CSV</label>
              <input
                type="file"
                accept=".csv"
                id="expenseCsv"
                onChange={(e) => handleFileUpload(e, "expense")}
              />
              {budgetExpense.length > 0 && (
                <span className="done">
                  {budgetExpense.length} expense items loaded
                </span>
              )}
            </div>
          </S.Uploads>
        </S.Step>
      )}

      {step === 3 && (
        <S.Step>
          <h2>Where does your cash stand today?</h2>
          <p>Rough numbers are fine — you can change these any time.</p>
          <S.Balances>
            <label>
              Checking balance
              <input
                type="number"
                value={checking}
                placeholder="0"
                onChange={(e) => setChecking(e.target.value)}
              />
            </label>
            <label>
              {labels.bufferLabel}
              {mode === "regular" ? " / savings" : " (savings for slow months)"}
              <input
                type="number"
                value={buffer}
                placeholder="0"
                onChange={(e) => setBuffer(e.target.value)}
              />
            </label>
            {mode !== "regular" && (
              <label>
                Money already set aside for taxes
                <input
                  type="number"
                  value={taxReserve}
                  placeholder="0"
                  onChange={(e) => setTaxReserve(e.target.value)}
                />
              </label>
            )}
          </S.Balances>
        </S.Step>
      )}

      {step > 0 && (
        <S.Nav>
          <Button
            classType="text"
            handleClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            Back
          </Button>
          {step < 3 ? (
            <Button
              buttonSize="medium"
              disabled={!stepValid}
              handleClick={() => setStep((s) => s + 1)}
            >
              Next
            </Button>
          ) : (
            <Button buttonSize="medium" handleClick={finish}>
              Start tracking my cash flow
            </Button>
          )}
        </S.Nav>
      )}

      {error && <S.ErrorText>{error}</S.ErrorText>}

      <SessionExpired
        isOpen={isSessionExpired}
        closeModal={setIsSessionExpired}
      />
    </S.Wrapper>
  );
};

export default Onboarding;
